import {
  Box,
  useColorModeValue,
  Drawer,
  DrawerContent,
  useDisclosure,
  usePrefersReducedMotion,
} from '@chakra-ui/react'
import { Outlet } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { Sidebar, type SidebarTransitionPhase } from './Sidebar'
import { Header } from './Header'
import { useSidebarState } from '@/hooks/useSidebarState'
import { useNetworkEnvironment, type NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import StripeTransition from '@/components/common/StripeTransition'

const SIDEBAR_COLLAPSE_FADE_MS = 160
const SIDEBAR_EXPAND_MS = 240

export function Layout() {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isCollapsed, setIsCollapsed } = useSidebarState()
  const [sidebarPhase, setSidebarPhase] = useState<SidebarTransitionPhase>(
    isCollapsed ? 'collapsed' : 'expanded'
  )
  const sidebarTransitionTimerRef = useRef<number | null>(null)
  const sidebarTransitioningRef = useRef(false)
  const prefersReducedMotion = usePrefersReducedMotion()
  const bg = useColorModeValue('gray.50', 'gray.900')
  const { currentEnv } = useNetworkEnvironment()
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transitionTarget, setTransitionTarget] = useState<NetworkEnvironment | null>(null)
  const previousEnvRef = useRef<NetworkEnvironment | null>(null)

  // Calculate left margin for main content area
  const mainContentMargin = isCollapsed ? 16 : 60

  useEffect(() => {
    if (sidebarTransitioningRef.current) {
      return
    }
    setSidebarPhase(isCollapsed ? 'collapsed' : 'expanded')
  }, [isCollapsed])

  useEffect(() => {
    return () => {
      if (sidebarTransitionTimerRef.current !== null) {
        window.clearTimeout(sidebarTransitionTimerRef.current)
      }
    }
  }, [])

  const toggleSidebar = () => {
    if (sidebarTransitioningRef.current) {
      return
    }

    const nextCollapsed = !isCollapsed
    if (prefersReducedMotion) {
      setIsCollapsed(nextCollapsed)
      setSidebarPhase(nextCollapsed ? 'collapsed' : 'expanded')
      return
    }

    sidebarTransitioningRef.current = true
    if (nextCollapsed) {
      // Hide labels first; only then contract the rail and move the content.
      setSidebarPhase('collapsing')
      sidebarTransitionTimerRef.current = window.setTimeout(() => {
        setIsCollapsed(true)
        setSidebarPhase('collapsed')
        sidebarTransitioningRef.current = false
        sidebarTransitionTimerRef.current = null
      }, SIDEBAR_COLLAPSE_FADE_MS)
      return
    }

    // Expand the rail first; labels become visible after it settles.
    setIsCollapsed(false)
    setSidebarPhase('expanding')
    sidebarTransitionTimerRef.current = window.setTimeout(() => {
      setSidebarPhase('expanded')
      sidebarTransitioningRef.current = false
      sidebarTransitionTimerRef.current = null
    }, SIDEBAR_EXPAND_MS)
  }

  const handleTransitionComplete = () => {
    setIsTransitioning(false)
    setTransitionTarget(null)
  }

  useEffect(() => {
    if (!previousEnvRef.current) {
      previousEnvRef.current = currentEnv
      return
    }

    if (previousEnvRef.current !== currentEnv) {
      setTransitionTarget(currentEnv)
      setIsTransitioning(true)
      previousEnvRef.current = currentEnv
    }
  }, [currentEnv])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }
    document.body.dataset.network = currentEnv
    return () => {
      if (document.body.dataset.network === currentEnv) {
        delete document.body.dataset.network
      }
    }
  }, [currentEnv])

  return (
    <Box minH="100dvh" bg={bg}>
      <StripeTransition
        isActive={isTransitioning && Boolean(transitionTarget)}
        onComplete={handleTransitionComplete}
        stripeCount={8}
        duration={1.2}
        delay={0.015}
        direction={transitionTarget === 'mainnet' ? 'vertical-down' : 'vertical-up'}
        colorScheme={
          transitionTarget === 'mainnet'
            ? 'blue'
            : 'linear-gradient(135deg, #ff4b2b 0%, #ff416c 100%)'
        }
        zIndex={2000}
      />
      {currentEnv === 'testnet' && (
        <Box
          position="fixed"
          inset={0}
          border="4px solid #e74c3c"
          pointerEvents="none"
          zIndex={30}
        />
      )}
      {/* Sidebar for desktop */}
      <Sidebar 
        onClose={onClose} 
        display={{ base: 'none', md: 'flex' }}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleSidebar}
        showToggle={true}
        transitionPhase={sidebarPhase}
        top={{ base: 0, md: '14' }}
        h={{ base: 'full', md: 'calc(100vh - 3.5rem)' }}
      />
      
      {/* Mobile drawer */}
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <Sidebar onClose={onClose} transitionPhase="expanded" />
        </DrawerContent>
      </Drawer>

      {/* Header */}
      <Header onOpen={onOpen} />
      
      {/* Main content */}
      <Box 
        ml={{ base: 0, md: mainContentMargin }} 
        pt="14"
        minH="100dvh"
        bg={bg}
        transition="margin-left 0.3s ease"
      >
        <Box p={{ base: 4, md: 6, lg: 8 }} minH="calc(100dvh - 3.5rem)" bg={bg}>
          <Outlet key={currentEnv} />
        </Box>
      </Box>

    </Box>
  )
}
